import { readColumnVisibility, saveColumnVisibility } from '@/components/table/column-preferences';
import { usePatientConfiguration } from '@/features/patient-configuration/api';
import { CustomFieldDialog } from '@/features/patient-configuration/CustomFields';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuGroup,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { clinicalAccessError } from './accessError';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { flowsheetUrl } from './api';
import { formatGa } from './calculations';
import { clientApi } from '@/config/axios';
import { useOrgTimezone } from '@/lib/timezone';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth/AuthContext';
import { usePermissions } from '@/features/permissions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
export const rosterColumns = [
  ['name', 'Patient'],
  ['age', 'Age'],
  ['gtpal', 'GTPAL'],
  ['edd', 'EDD'],
  ['ga_days', 'GA'],
  ['delivery_status', 'Care status'],
  ['planned_birth_site', 'Birth site'],
  ['next_appointment', 'Next visit'],
  ['last_visit', 'Last visit'],
  ['overdue_count', 'Overdue'],
  ['open_alerts', 'Rule flags'],
  ['gbs', 'GBS'],
  ['rh', 'Rh'],
  ['assigned_midwife', 'Midwife'],
  ['risk_flags', 'Risks'],
  ['days_postpartum', 'Days postpartum'],
  ['newborn_names', 'Newborns'],
] as const;
const viewsList = [
  ['all', 'All'],
  ['due4', 'Due in next 4 weeks'],
  ['40weeks', 'At least 40 weeks'],
  ['labor', 'In labor'],
  ['postpartum1', 'Postpartum week 1'],
  ['overdue', 'Overdue checklists'],
  ['sixweek', 'Postpartum weeks 5–7'],
  ['gbs', 'GBS unknown from 36 weeks'],
  ['rh', 'Rh negative, prophylaxis not documented'],
] as const;
type Row = Record<string, unknown> & {
  pregnancy_id: string;
  patient_id: string;
  name: string | null;
  medical_record_number: string;
  ga_days: number | null;
  status_source: string;
};
export type RosterView = { status: string; filter: string; sort: string[]; search: string };
export const defaultRosterView: RosterView = { status: 'active', filter: 'all', sort: ['edd'], search: '' };
export function CareRoster({
  command,
  onViewChange,
}: {
  command?: { id: string; view: RosterView };
  onViewChange?: (view: RosterView) => void;
} = {}) {
  const { organization, user } = useAuth();
  const { permissions } = usePermissions();
  const { timeZone } = useOrgTimezone();
  const navigate = useNavigate();
  const org = organization?.organization_id;
  const [open, setOpen] = useState(false);
  const config = usePatientConfiguration();
  const [fieldDialog, setFieldDialog] = useState(false);
  const allColumns: ReadonlyArray<readonly [string, string]> = [
    ...rosterColumns,
    ...(config.data?.fields ?? []).map((f) => [`custom:${f.id}`, f.label] as const),
  ];
  const preferenceKey = `roster-columns:${org}:${user?.id}`;
  const [hidden, setHidden] = useState<string[]>(() =>
    Object.entries(readColumnVisibility(preferenceKey))
      .filter(([, v]) => !v)
      .map(([k]) => k),
  );
  useEffect(() => {
    setHidden(
      Object.entries(readColumnVisibility(preferenceKey))
        .filter(([, v]) => !v)
        .map(([k]) => k),
    );
  }, [preferenceKey]);
  const changeVisibility = (key: string, visible: boolean) =>
    setHidden((old) => {
      const next = visible ? old.filter((k) => k !== key) : [...old, key];
      saveColumnVisibility(preferenceKey, Object.fromEntries(next.map((k) => [k, false])));
      return next;
    });
  const visibleColumns = allColumns.filter(([key]) => !hidden.includes(key));
  const [status, setStatus] = useState('active');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState(['edd']);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    if (!command) return;
    setStatus(command.view.status);
    setFilter(command.view.filter);
    setSort(command.view.sort);
    setSearch(command.view.search);
    setOffset(0);
    setOpen(true);
  }, [command]);
  useEffect(() => {
    onViewChange?.({ status, filter, sort, search });
  }, [status, filter, sort, search, onViewChange]);
  const dateParts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    day: '2-digit',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(new Date());
  const part = (type: string) => dateParts.find((p) => p.type === type)?.value;
  const today = `${part('year')}-${part('month')}-${part('day')}`;
  const query = useQuery({
    enabled: !!org && open && permissions.includes('read_flowsheet'),
    queryKey: ['midwifery-roster', org, status, filter, sort, search, offset, today, command?.id],
    queryFn: async () =>
      (
        await clientApi.get<{ total: number; data: Row[] }>(`${flowsheetUrl(org!)}/roster`, {
          params: { status, filter, search, offset, limit: 50, on_date: today, sort: sort.join(',') },
        })
      ).data,
  });
  if (!permissions.includes('read_flowsheet')) return null;
  const changeSort = (column: string, multi: boolean) => {
    setSort((old) => {
      const existing = old.find((k) => k.replace('-', '') === column);
      const next = existing === column ? `-${column}` : column;
      return multi ? [...old.filter((k) => k.replace('-', '') !== column), next].slice(-5) : [next];
    });
    setOffset(0);
  };
  return (
    <Card>
      <CustomFieldDialog open={fieldDialog} onOpenChange={setFieldDialog} />
      <CardHeader>
        <CardTitle>Pregnancy roster</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button variant="outline" onClick={() => setOpen(!open)}>
          {open ? 'Hide' : 'Show'} pregnancy roster
        </Button>
        {open && (
          <>
            <p className="text-sm text-muted-foreground">
              Clinical care status takes precedence when recorded; otherwise status comes from the pregnancy record.
              Missing data stays blank. Rule flags are configured findings, not acknowledged alerts. Shift-click a
              column to add a sort key.
            </p>
            <div className="flex flex-wrap gap-3">
              <label>
                Status{' '}
                <select
                  value={status}
                  className="min-h-11 border"
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setOffset(0);
                  }}>
                  {[
                    'active',
                    'all',
                    'PRENATAL',
                    'TERM',
                    'POST_DATES',
                    'IN_LABOR',
                    'POSTPARTUM',
                    'TRANSFERRED',
                    'CLOSED',
                    'PREGNANCY_LOSS',
                  ].map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label>
                Filter{' '}
                <select
                  value={filter}
                  className="min-h-11 border"
                  onChange={(e) => {
                    setFilter(e.target.value);
                    setOffset(0);
                    if (e.target.value !== 'all') setStatus('all');
                  }}>
                  {viewsList.map(([id, label]) => (
                    <option key={id} value={id}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2">
                Find patient{' '}
                <Input
                  value={search}
                  className="w-56"
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setOffset(0);
                  }}
                />
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">Columns</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuGroup>
                    {allColumns.map(([key, label]) => (
                      <DropdownMenuCheckboxItem
                        key={key}
                        checked={!hidden.includes(key)}
                        onSelect={(e) => e.preventDefault()}
                        disabled={visibleColumns.length === 1 && !hidden.includes(key)}
                        onCheckedChange={(visible) => changeVisibility(key, !!visible)}>
                        {label}
                      </DropdownMenuCheckboxItem>
                    ))}
                    {permissions.includes('manage_organization') && (
                      <DropdownMenuItem onSelect={() => setFieldDialog(true)}>Create custom column…</DropdownMenuItem>
                    )}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <p className="text-sm">Sort: {sort.join(', ')}</p>
            {query.isError && (
              <p role="alert">
                {clinicalAccessError(query.error, 'Roster unavailable.')}{' '}
                <button onClick={() => void query.refetch()}>Retry</button>
              </p>
            )}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {visibleColumns.map(([key, label]) => (
                      <TableHead key={key}>
                        <button className="min-h-11 whitespace-nowrap" onClick={(e) => changeSort(key, e.shiftKey)}>
                          {label}
                          {sort.includes(key) ? ' ↑' : sort.includes(`-${key}`) ? ' ↓' : ''}
                        </button>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {query.data?.data.map((row) => (
                    <TableRow key={row.pregnancy_id}>
                      {visibleColumns.map(([key]) => (
                        <TableCell key={key} className="whitespace-nowrap">
                          {key.startsWith('custom:') ? (
                            String((row.custom_fields as Record<string, string> | undefined)?.[key.slice(7)] || '—')
                          ) : key === 'name' ? (
                            <Button
                              variant="link"
                              onClick={() =>
                                navigate({ to: '/patients/$patientId', params: { patientId: row.patient_id } })
                              }>
                              {row.name || row.medical_record_number}
                            </Button>
                          ) : key === 'ga_days' ? (
                            formatGa(row.ga_days)
                          ) : key === 'delivery_status' ? (
                            <span title={`Source: ${row.status_source}`}>{String(row[key])}</span>
                          ) : Array.isArray(row[key]) ? (
                            (row[key] as string[]).join(', ') || '—'
                          ) : row[key] === null || row[key] === undefined ? (
                            '—'
                          ) : (
                            String(row[key])
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex gap-2 items-center">
              <Button variant="outline" disabled={!offset} onClick={() => setOffset(Math.max(0, offset - 50))}>
                Previous
              </Button>
              <span>{query.data?.total ?? 0} pregnancies</span>
              <Button
                variant="outline"
                disabled={!query.data || offset + 50 >= query.data.total}
                onClick={() => setOffset(offset + 50)}>
                Next
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
